import { api } from './api';
import { AcceptedLogin, LoginMethod, OrganisationVanityLogin } from '@/types';

const loginApi = api.injectEndpoints({
  endpoints: (builder) => ({
    session: builder.query<AcceptedLogin, void>({
      query: () => 'authenticate',
      transformResponse: (response: any) => {
        const source = response?.source ?? 'session';
        return { ...response, source };
      },
    }),
    authenticateSsoExchange: builder.mutation<AcceptedLogin, SsoExchange>({
      query: (v: SsoExchange) => ({
        url: 'login/exchange',
        method: 'POST',
        body: v,
        credentials: 'include',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(({ data: session }) => {
            const source = 'login';
            dispatch(
              loginApi.util.upsertQueryData('session', undefined, {
                ...session,
                source,
              }),
            );
          })
          .catch(() => {});
      },
    }),
    authenticate: builder.mutation<
      AuthenticationResponse,
      AuthenticationRequest
    >({
      query: ({ email, password }) => ({
        url: 'login/credentials',
        method: 'POST',
        body: {
          email,
          password,
        },
        credentials: 'include',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(({ data: session }) => {
            const source = 'login';
            dispatch(
              loginApi.util.upsertQueryData('session', undefined, {
                ...session,
                source,
              }),
            );
          })
          .catch(() => {});
      },
    }),
    introspect: builder.mutation<IntrospectResponse, IntrospectRequest>({
      query: ({ email }) => ({
        url: 'login/introspect',
        method: 'POST',
        body: {
          email,
        },
      }),
    }),
    // `to` is where the browser goes once the session is gone; the log-in
    // page by default. A landing that sends someone to log in as another
    // account passes the emailed link's token along.
    logout: builder.mutation<void, { to?: string } | void>({
      query: () => ({
        url: 'logout',
        method: 'POST',
      }),
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        await queryFulfilled;
        localStorage.removeItem('kinesis:session');
        await dispatch(api.util.resetApiState());
        window.location.assign(arg?.to ?? '/');
      },
    }),
    getVanityLogin: builder.query<OrganisationVanityLogin, string>({
      query: (code: string) => `organisations/${code}/login`,
    }),
  }),
});

type AuthenticationRequest = {
  email: string;
  password: string;
};

type AuthenticationResponse = AcceptedLogin;

type IntrospectRequest = {
  email: string;
};

type IntrospectResponse = LoginMethod;

type SsoExchange = {
  state: string;
  code: string;
};

export const {
  useAuthenticateSsoExchangeMutation,
  useAuthenticateMutation,
  useIntrospectMutation,
  useSessionQuery,
  useLogoutMutation,
  useGetVanityLoginQuery,
} = loginApi;

export { loginApi };
